import React from "react";
import { ROUTES } from "../../constants/routes";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { MdDelete } from "react-icons/md";
import { cn } from "../../lib/utils";
import { Input } from "../ui/input";

const TemplateLocationCard = ({
  link, // link object.
  template,
  printLocations, // print locations array.
  usedLocations, // used locations array.
  onRemoveTemplate, // function to remove template from selected templates.
  onChangePrintLocation, // function to change print location of a template.
  // make possible to clear print location of a template.
  // so that it can be selected again by other template.
  onOrderChange,
}: any) => {
  const openTemplateInNewTab = (templateId: string) => {
    window.open(`${ROUTES.DESIGN}/${templateId}`, "_blank");
  };

  return (
    <div className="flex w-full">
      <div className="w-3/5">
        {template && (
          <div className="flex justify-between ">
            <div className="flex gap-x-2 min-w-[400px]">
              <div className="group flex w-min hover:cursor-zoom-in relative">
                <img
                  src={template.previewImage}
                  alt="template"
                  className="h-[150px] max-w-[150px] object-cover rounded-md border border-white bg-borderColor"
                />

                <div className="z-20 opacity-0 group-hover:opacity-100 fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 bg-borderColor border border-white rounded w-[500px] h-[500px] transition-opacity duration-0 ease-in group-hover:duration-500 group-hover:ease-out pointer-events-none">
                  <img
                    src={template.previewImage}
                    alt="template"
                    className="w-full h-full object-contain rounded-md"
                  />
                </div>
              </div>

              <div className="px-2 flex flex-col gap-y-2">
                <span
                  title="Open in new tab"
                  className="text-lg font-bold hover:cursor-pointer hover:text-primary"
                  onClick={() => openTemplateInNewTab(template._id)}
                >
                  {template.internalName}
                </span>
                <span title="Template Slug" className="text-sm">
                  {template.slug}
                </span>

                {link.printLocation && (
                  <span
                    title="Template Slug"
                    className="text-sm text-green-300"
                  >
                    {link.printLocation}
                  </span>
                )}

                {!link.printLocation && (
                  <span title="Template Slug" className="text-sm text-red-600">
                    Location not set
                  </span>
                )}
              </div>
            </div>

            <div className="flex w-1/3 relative items-center ">
              <div className="absolute z-10 left-2 -top-0 flex items-center justify-end text-xs ">
                <div
                  className="cursor-pointer me-1 text-primary hover:text-primary/80"
                  onClick={() => onChangePrintLocation(template._id, "")}
                >
                  Clear Selection
                </div>
              </div>

              <ScrollArea.Root className="w-full relative h-[100px] overflow-hidden ">
                <ScrollArea.Viewport className="w-full h-full px-2 me-4 relative rounded">
                  <div className="flex items-end justify-start">
                    <RadioGroup.Root
                      value={link.printLocation}
                      onValueChange={(newValue) => {
                        onChangePrintLocation(template._id, newValue);
                      }}
                    >
                      {printLocations.map(
                        (printLocation: any, index: number) => {
                          return (
                            <div
                              key={printLocation._id}
                              className="flex w-full items-center justify-start my-2 gap-x-2"
                            >
                              <RadioGroup.Item
                                key={printLocation._id}
                                className="data-[disabled]:opacity-30 data-[disabled]:cursor-not-allowed size-[25px] cursor-default rounded-full bg-black outline-none hover:bg-primary/90 hover:cursor-pointer"
                                value={printLocation.slug}
                                id={printLocation.slug}
                                disabled={
                                  usedLocations.includes(printLocation.slug) &&
                                  printLocation.slug !== link.printLocation
                                }
                              >
                                <RadioGroup.Indicator className="relative flex size-full items-center justify-center after:block after:size-[11px] after:rounded-full after:bg-primary" />
                              </RadioGroup.Item>

                              <label
                                className={cn(
                                  "text-[15px] leading-none text-white",
                                  usedLocations.includes(printLocation.slug) &&
                                    printLocation.slug !== link.printLocation &&
                                    "text-gray-600"
                                )}
                              >
                                {printLocation.slug}
                              </label>
                            </div>
                          );
                        }
                      )}
                    </RadioGroup.Root>
                  </div>
                </ScrollArea.Viewport>

                {/* Ensure scrollbar stays inside the scrollable box */}
                <ScrollArea.Scrollbar
                  className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5"
                  orientation="vertical"
                >
                  <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px]" />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </div>

            <div className="flex items-start justify-start px-4">
              <div>
                <span className="text-[12px]">Order</span>

                <Input
                  type="number"
                  id={"order"}
                  placeholder={"Order"}
                  value={link?.order}
                  min={1}
                  className="input-ring h-8 bg-transparent text-white placeholder w-20"
                  onChange={(e) =>
                    onOrderChange(template._id, Number(e.target.value))
                  }
                />
              </div>
            </div>

            <div className="flex items-start mt-6 ps-4 justify-start">
              <MdDelete
                title="Delete"
                className="hover:text-red-600 cursor-pointer"
                size={30}
                onClick={onRemoveTemplate}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateLocationCard;
